import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Box, Typography, IconButton, Avatar, TextField, Button, CircularProgress, Switch,
  Dialog, DialogTitle, DialogContent, DialogActions, Tooltip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import moment from "moment";
import { useSnackbar } from "notistack";
import CenteredDetailModal from "./CenteredDetailModal";
import { getRoleDisplay } from "../utils/roleDisplay";
import {
  update_school_staff_api, send_staff_password_reset_api, delete_school_staff_api,
  get_staff_overview_api, upload_staff_photo_api, update_student_task_api,
} from "../DAL/SchoolPortal/SchoolPortal";
import { update_instructor_profile_api } from "../DAL/Tutors/Tutors";
import BookingActions from "../pages/SchoolPortal/Tutors/BookingActions";
import { prettyName } from "../pages/SchoolPortal/StudentRecord/recordTheme";
import { useSchool } from "../Hooks/SchoolContext";
import {
  bg, surface, surfaceRaised, border, textPrimary, textSecondary, textMuted, accent,
  font, success, warning, error as errorColor, shadow, avatarColorFor,
  primaryButtonSx, secondaryButtonSx, inputSx, dialogPaperSx,
} from "../layouts/schoolPortal/schoolPortalTheme";

/**
 * A member of staff, as the person running the place needs them.
 *
 * WHAT THIS REPLACED
 * A name, an email, a status and a delete button. Nothing about their work:
 * not what they earned, what was in their diary, what they had set students,
 * or where they were stuck in setup — so "is Dana keeping up" meant logging in
 * as Dana. Everything here comes from one server response, including what the
 * viewer may do, so no button is offered that the server would then refuse.
 */

const TABS = [
  { id: "overview", label: "Overview" },
  { id: "upcoming", label: "Coming up" },
  { id: "tasks", label: "Tasks" },
  { id: "setup", label: "Setup" },
  { id: "account", label: "Account" },
];

const money = (amount, currency) => {
  try {
    return new Intl.NumberFormat(currency === "USD" ? "en-US" : "en-GB", {
      style: "currency", currency: currency || "GBP",
    }).format(Number(amount) || 0);
  } catch (e) {
    return `${currency || ""} ${Number(amount || 0).toFixed(2)}`.trim();
  }
};

const STEP_COPY = {
  photo: ["Profile photo", "You can add one on the Account tab."],
  connect: ["Payout account", "They have to finish this themselves in Stripe."],
  rates: ["Their rates", "They set these under Teaching → Profile & rates."],
  students: ["A student has joined", null],
  calendar: ["Taking bookings", "Switch it on below once their hours are right."],
};

const CONNECT_STATE = {
  complete: "done", needs_info: "Stripe needs more information from them",
  in_review: "submitted, Stripe is reviewing it", not_started: "not started", unknown: "could not check Stripe",
};

const BOOKING_STATUS = {
  confirmed: ["Confirmed", success],
  pending_instructor_approval: ["Awaiting them", warning],
  pending_student_confirmation: ["Awaiting the student", warning],
};

function Card({ title, children, right }) {
  return (
    <Box sx={{ backgroundColor: surface, border: `1px solid ${border}`, borderRadius: "12px", boxShadow: shadow, overflow: "hidden", mb: 1.5 }}>
      {title && (
        <Box sx={{ backgroundColor: surfaceRaised, borderBottom: `1px solid ${border}`, px: 1.5, py: 0.75, display: "flex", alignItems: "center" }}>
          <Typography sx={{ fontFamily: font, fontSize: "10.5px", fontWeight: 800, letterSpacing: "0.07em", textTransform: "uppercase", color: textSecondary, flex: 1 }}>
            {title}
          </Typography>
          {right}
        </Box>
      )}
      <Box sx={{ px: 1.5, py: 1.25 }}>{children}</Box>
    </Box>
  );
}

function Stat({ label, value, sub, tone }) {
  return (
    <Box sx={{ backgroundColor: surfaceRaised, borderRadius: "8px", px: 1.1, py: 0.85, minWidth: 0 }}>
      <Typography sx={{ fontFamily: font, fontSize: "18px", fontWeight: 800, color: tone || textPrimary, lineHeight: 1.1, fontVariantNumeric: "tabular-nums" }}>
        {value}
      </Typography>
      <Typography sx={{ fontFamily: font, fontSize: "11px", color: textSecondary, mt: 0.25 }}>{label}</Typography>
      {sub && <Typography sx={{ fontFamily: font, fontSize: "10.5px", color: textMuted }}>{sub}</Typography>}
    </Box>
  );
}

const Empty = ({ children }) => (
  <Typography sx={{ fontFamily: font, fontSize: "12.5px", color: textMuted, py: 1 }}>{children}</Typography>
);

export default function SchoolStaffDetailDrawer({
  open, onClose, staffMember, onUpdate, progress = null, windowDays = 30,
}) {
  const { enqueueSnackbar } = useSnackbar();
  const { hasModule } = useSchool();
  const [tab, setTab] = useState("overview");
  const [state, setState] = useState({ loading: true, data: null, error: null });
  const [busy, setBusy] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const [form, setForm] = useState({ firstName: "", lastName: "", email: "" });
  const photoInput = useRef(null);

  const staffId = staffMember?._id;

  const load = useCallback(async () => {
    if (!staffId) return;
    try {
      const r = await get_staff_overview_api(staffId, windowDays);
      if (r?.code === 200) {
        setState({ loading: false, data: r.data, error: null });
        setForm({ firstName: r.data.profile.firstName, lastName: r.data.profile.lastName, email: r.data.profile.email });
      } else {
        setState({ loading: false, data: null, error: r?.message || "Could not load this profile" });
      }
    } catch (e) {
      setState({ loading: false, data: null, error: "Could not load this profile" });
    }
  }, [staffId, windowDays]);

  useEffect(() => {
    if (!open || !staffId) return;
    setTab("overview");
    setState({ loading: true, data: null, error: null });
    load();
  }, [open, staffId, load]);

  const d = state.data;
  const p = d?.permissions || {};
  const profile = d?.profile;

  /** Run an action, report it, refresh the profile and — if it changes the table — the list. */
  const act = async (key, fn, { success: ok, refreshList = false, closeAfter = false } = {}) => {
    setBusy(key);
    try {
      const r = await fn();
      if (r?.code === 200) {
        if (ok) enqueueSnackbar(ok, { variant: "success" });
        if (refreshList) onUpdate?.();
        if (closeAfter) onClose?.();
        else await load();
      } else {
        enqueueSnackbar(r?.message || "That didn't work", { variant: "error" });
      }
    } catch (e) {
      enqueueSnackbar("That didn't work", { variant: "error" });
    } finally {
      setBusy(null);
      setConfirm(null);
    }
  };

  const onPhoto = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!/^image\/(jpeg|png|webp)$/.test(file.type)) {
      enqueueSnackbar("The photo needs to be a JPEG, PNG or WebP", { variant: "error" });
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      enqueueSnackbar("The photo needs to be under 5MB", { variant: "error" });
      return;
    }
    const fd = new FormData();
    fd.append("photo", file);
    act("photo", () => upload_staff_photo_api(profile.id, fd), { success: "Photo updated", refreshList: true });
  };

  if (!staffMember) return null;

  const name = profile?.name || [staffMember.first_name, staffMember.last_name].filter(Boolean).join(" ") || staffMember.email;
  const status = profile?.status || staffMember.status || "active";
  const suspended = status === "suspended";

  // ── confirmations: every destructive act says what it does before it does it ──
  const CONFIRMS = {
    reset: {
      title: "Reset their password?",
      body: `${name} gets an email with a link to set a new password. They cannot sign in until they do.`,
      cta: "Send the reset link",
      run: () => act("reset", () => send_staff_password_reset_api(profile.id), { success: "Reset link sent" }),
    },
    suspend: {
      title: suspended ? "Reactivate this account?" : "Suspend this account?",
      body: suspended
        ? `${name} can sign in again straight away.`
        : `${name} is signed out and cannot sign in until you reactivate them. Nothing is deleted — their sessions, students and history stay as they are.`,
      cta: suspended ? "Reactivate" : "Suspend",
      danger: !suspended,
      run: () => act("suspend", () => update_school_staff_api(profile.id, { status: suspended ? "active" : "suspended" }),
        { success: suspended ? "Account reactivated" : "Account suspended", refreshList: true }),
    },
    delete: {
      title: "Delete this staff member?",
      body: `This permanently removes ${name}'s account. It cannot be undone. If you only want to stop them signing in, suspend them instead.`,
      cta: "Delete permanently",
      danger: true,
      run: () => act("delete", () => delete_school_staff_api(profile.id), { success: "Staff member deleted", refreshList: true, closeAfter: true }),
    },
  };
  const c = confirm ? (CONFIRMS[confirm.kind] || confirm) : null;

  return (
    <CenteredDetailModal open={open} onClose={onClose} maxWidth={660}>
      {/* ── header ─────────────────────────────────────────────────────── */}
      <Box sx={{ backgroundColor: surfaceRaised, borderBottom: `1px solid ${border}`, px: 2.5, pt: 2, pb: 0 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1.5 }}>
          <Avatar src={profile?.photoUrl || undefined} sx={{ width: 48, height: 48, bgcolor: avatarColorFor(name), fontFamily: font, fontWeight: 700 }}>
            {name.split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase()}
          </Avatar>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography noWrap sx={{ fontFamily: font, fontSize: "17px", fontWeight: 800, color: textPrimary }}>{name}</Typography>
            <Box sx={{ display: "flex", gap: 0.75, mt: 0.4, flexWrap: "wrap" }}>
              <Tooltip title={getRoleDisplay(profile?.role || staffMember.role).powers} arrow>
                <Box sx={{ px: 0.8, py: 0.1, borderRadius: "5px", fontFamily: font, fontSize: "11px", fontWeight: 700, color: accent, backgroundColor: `${accent}14`, cursor: "help" }}>
                  {getRoleDisplay(profile?.role || staffMember.role).label}
                </Box>
              </Tooltip>
              <Box sx={{
                px: 0.8, py: 0.1, borderRadius: "5px", fontFamily: font, fontSize: "11px", fontWeight: 700, textTransform: "capitalize",
                color: suspended ? errorColor : status === "active" ? success : textSecondary,
                backgroundColor: suspended ? `${errorColor}14` : status === "active" ? `${success}14` : surface,
              }}>
                {status}
              </Box>
            </Box>
          </Box>
          <IconButton onClick={onClose} size="small" sx={{ color: textMuted }}><CloseIcon /></IconButton>
        </Box>
        <Box sx={{ display: "flex", gap: 0.25, overflowX: "auto" }}>
          {TABS.map((t) => (
            <Box
              key={t.id} role="tab" tabIndex={0} aria-selected={tab === t.id}
              onClick={() => setTab(t.id)}
              onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setTab(t.id); } }}
              sx={{
                px: 1.4, py: 0.9, cursor: "pointer", whiteSpace: "nowrap", fontFamily: font, fontSize: "13px", fontWeight: 700,
                color: tab === t.id ? accent : textSecondary, borderBottom: `2.5px solid ${tab === t.id ? accent : "transparent"}`,
              }}
            >
              {t.label}
              {t.id === "upcoming" && d?.upcoming?.length ? ` (${d.upcoming.length})` : ""}
              {t.id === "tasks" && d?.tasks?.length ? ` (${d.tasks.length})` : ""}
            </Box>
          ))}
        </Box>
      </Box>

      {/* ── body ───────────────────────────────────────────────────────── */}
      <Box sx={{ backgroundColor: bg, p: 2, overflowY: "auto", flex: 1 }}>
        {state.loading && <Box sx={{ py: 5, textAlign: "center" }}><CircularProgress size={22} sx={{ color: accent }} /></Box>}
        {state.error && <Typography sx={{ fontFamily: font, fontSize: "13px", color: errorColor }}>{state.error}</Typography>}

        {d && tab === "overview" && (
          <>
            <Card title={`Teaching — last ${windowDays} days`}>
              {progress ? (
                <>
                  <Box sx={{ display: "grid", gridTemplateColumns: { xs: "repeat(2, 1fr)", sm: "repeat(4, 1fr)" }, gap: 0.75 }}>
                    <Stat label="students" value={progress.studentsInWindow} sub={`${progress.studentsAll} ever`} />
                    <Stat label="sessions" value={progress.sessionsTaught} sub={`${progress.hoursTaught}h`} />
                    <Stat label="debriefs owed" value={progress.debriefsOwed} tone={progress.debriefsOwed ? warning : success} />
                    <Stat label="coming up" value={progress.upcoming} sub={progress.awaitingAnswer ? `${progress.awaitingAnswer} to answer` : null} />
                  </Box>
                  <Typography sx={{ fontFamily: font, fontSize: "11.5px", color: textSecondary, mt: 1 }}>
                    {[
                      `${progress.signedOff} lesson${progress.signedOff === 1 ? "" : "s"} signed off`,
                      progress.notYet ? `${progress.notYet} not yet` : null,
                      `${progress.notesLogged} note${progress.notesLogged === 1 ? "" : "s"}`,
                      progress.lastTaughtAt ? `last taught ${moment(progress.lastTaughtAt).fromNow()}` : "has not taught yet",
                    ].filter(Boolean).join(" · ")}
                  </Typography>
                </>
              ) : <Empty>No teaching activity on record.</Empty>}
            </Card>

            {d.earnings && (
              <Card title="Money">
                <Box sx={{ display: "grid", gridTemplateColumns: { xs: "repeat(2, 1fr)", sm: "repeat(3, 1fr)" }, gap: 0.75 }}>
                  <Stat label={`paid out, last ${d.windowDays} days`} value={money(d.earnings.paidInWindow, d.earnings.currency)} />
                  <Stat label="paid out in total" value={money(d.earnings.paidTotal, d.earnings.currency)} />
                  {/* Their share is only fixed when the payout runs, so this
                      is the ceiling, not the amount they will receive. */}
                  <Stat label="still to pay out" value={money(d.earnings.dueBeforeFees, d.earnings.currency)} sub="before fees — the most it can be" />
                </Box>
                {d.earnings.failedCount > 0 && (
                  <Typography sx={{ fontFamily: font, fontSize: "12px", color: errorColor, fontWeight: 700, mt: 1 }}>
                    {d.earnings.failedCount} payout{d.earnings.failedCount === 1 ? "" : "s"} failed ({money(d.earnings.failed, d.earnings.currency)}) — their payout account needs attention.
                  </Typography>
                )}
                {d.earnings.lastPayouts.length > 0 && (
                  <Box sx={{ mt: 1.25 }}>
                    {d.earnings.lastPayouts.map((x) => (
                      <Box key={x.bookingId} sx={{ display: "flex", py: 0.4, borderTop: `1px solid ${border}` }}>
                        <Typography sx={{ fontFamily: font, fontSize: "12px", color: textSecondary, flex: 1 }}>
                          Session on {moment(x.at).format("D MMM")} · paid {moment(x.paidAt).format("D MMM")}
                        </Typography>
                        <Typography sx={{ fontFamily: font, fontSize: "12px", fontWeight: 700, color: textPrimary, fontVariantNumeric: "tabular-nums" }}>
                          {money(x.amount, d.earnings.currency)}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                )}
              </Card>
            )}
          </>
        )}

        {d && tab === "upcoming" && (
          <Card title="Their diary">
            {!d.upcoming.length && <Empty>Nothing in their diary.</Empty>}
            {d.upcoming.map((s) => {
              const [label, tone] = BOOKING_STATUS[s.status] || [s.status, textSecondary];
              return (
                <Box key={s.id} sx={{ display: "flex", alignItems: "center", gap: 1, py: 0.9, borderTop: `1px solid ${border}`, "&:first-of-type": { borderTop: "none", pt: 0 } }}>
                  <Box sx={{ width: 62, flexShrink: 0 }}>
                    <Typography sx={{ fontFamily: font, fontSize: "12.5px", fontWeight: 800, color: textPrimary }}>{moment(s.start).format("ddd D MMM")}</Typography>
                    <Typography sx={{ fontFamily: font, fontSize: "11.5px", color: textSecondary }}>{moment(s.start).format("HH:mm")}</Typography>
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography noWrap sx={{ fontFamily: font, fontSize: "13px", fontWeight: 700, color: textPrimary }}>{s.studentName}</Typography>
                    <Typography noWrap sx={{ fontFamily: font, fontSize: "11.5px", color: textSecondary }}>
                      {[s.subject ? prettyName(s.subject) : null, s.lesson != null ? `Lesson ${s.lesson}` : null, moment(s.start).fromNow()].filter(Boolean).join(" · ")}
                    </Typography>
                    {s.expiresAt && (
                      <Typography sx={{ fontFamily: font, fontSize: "11px", color: warning }}>expires {moment(s.expiresAt).fromNow()}</Typography>
                    )}
                  </Box>
                  <Box sx={{ px: 0.8, py: 0.15, borderRadius: "5px", fontFamily: font, fontSize: "10.5px", fontWeight: 700, color: tone, backgroundColor: `${tone}14`, whiteSpace: "nowrap" }}>
                    {label}
                  </Box>
                  {p.canCancelSessions && s.status === "confirmed" && (
                    <BookingActions
                      booking={{ _id: s.id, start_time: s.start, end_time: s.end, status: s.status, location_type: s.locationType, subject: s.subject }}
                      isManager canTeach={false} onDone={load}
                    />
                  )}
                </Box>
              );
            })}
          </Card>
        )}

        {d && tab === "tasks" && (
          <Card title="Work they have set">
            {!d.tasks.length && <Empty>No open tasks from them.</Empty>}
            {d.tasks.map((t) => (
              <Box key={t.id} sx={{ display: "flex", alignItems: "center", gap: 1, py: 0.9, borderTop: `1px solid ${border}`, "&:first-of-type": { borderTop: "none", pt: 0 } }}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography sx={{ fontFamily: font, fontSize: "13px", color: textPrimary, lineHeight: 1.35 }}>{t.title}</Typography>
                  <Typography sx={{ fontFamily: font, fontSize: "11.5px", color: textSecondary }}>
                    {t.studentName} · {t.dueAt
                      ? (new Date(t.dueAt) < new Date() ? `was due ${moment(t.dueAt).fromNow()}` : `due ${moment(t.dueAt).format("D MMM")}`)
                      : "no due date"}
                  </Typography>
                </Box>
                {t.canDismiss && (
                  <Button
                    size="small" disabled={busy === `task:${t.id}`}
                    onClick={() => setConfirm({
                      kind: "task",
                      title: "Cancel this task?",
                      body: `"${t.title}" is removed from ${t.studentName}'s list. They are not told.`,
                      cta: "Cancel the task",
                      danger: true,
                      run: () => act(`task:${t.id}`, () => update_student_task_api(t.studentId, t.id, "dismiss"), { success: "Task cancelled" }),
                    })}
                    sx={{ ...secondaryButtonSx, minHeight: 0, py: 0.4, px: 1.2, fontSize: "12px", color: errorColor }}
                  >
                    Cancel task
                  </Button>
                )}
              </Box>
            ))}
          </Card>
        )}

        {d && tab === "setup" && (
          <Card
            title="Setup"
            right={d.onboarding?.applies ? (
              <Typography sx={{ fontFamily: font, fontSize: "11px", fontWeight: 700, color: d.onboarding.complete ? success : warning }}>
                {d.onboarding.complete ? "Complete" : "Not finished"}
              </Typography>
            ) : null}
          >
            {!d.onboarding?.applies && <Empty>No setup steps apply to this role.</Empty>}
            {d.onboarding?.applies && d.onboarding.applicable.map((k) => {
              const done = Boolean(d.onboarding.steps[k]);
              const [label, hint] = STEP_COPY[k] || [k, null];
              const extra = k === "connect" && !done ? CONNECT_STATE[d.onboarding.connectState] : k === "students"
                ? `${d.onboarding.studentsJoined} joined of ${d.onboarding.studentCount} invited` : null;
              return (
                <Box key={k} sx={{ display: "flex", gap: 1, py: 0.8, borderTop: `1px solid ${border}`, "&:first-of-type": { borderTop: "none", pt: 0 } }}>
                  {done
                    ? <CheckCircleIcon sx={{ fontSize: 18, color: success, mt: "1px" }} />
                    : <RadioButtonUncheckedIcon sx={{ fontSize: 18, color: textMuted, mt: "1px" }} />}
                  <Box sx={{ flex: 1 }}>
                    <Typography sx={{ fontFamily: font, fontSize: "13px", fontWeight: 600, color: done ? textSecondary : textPrimary }}>{label}</Typography>
                    {(extra || (!done && hint)) && (
                      <Typography sx={{ fontFamily: font, fontSize: "11.5px", color: textMuted }}>{[extra, !done ? hint : null].filter(Boolean).join(" · ")}</Typography>
                    )}
                  </Box>
                </Box>
              );
            })}
            {p.canToggleBookable && hasModule("tutors") && (
              <Box sx={{ display: "flex", alignItems: "center", mt: 1.25, pt: 1, borderTop: `1px solid ${border}` }}>
                <Box sx={{ flex: 1 }}>
                  <Typography sx={{ fontFamily: font, fontSize: "13px", fontWeight: 700, color: textPrimary }}>Taking bookings</Typography>
                  <Typography sx={{ fontFamily: font, fontSize: "11.5px", color: textMuted }}>Students can only book them while this is on.</Typography>
                </Box>
                <Switch
                  checked={Boolean(profile.isBookable)} disabled={busy === "bookable"}
                  onChange={(e) => act("bookable", () => update_instructor_profile_api(profile.id, { isBookable: e.target.checked }),
                    { success: e.target.checked ? "They are taking bookings" : "Bookings are off" })}
                />
              </Box>
            )}
          </Card>
        )}

        {d && tab === "account" && (
          <>
            <Card title="Details">
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1.5 }}>
                <Avatar src={profile.photoUrl || undefined} sx={{ width: 56, height: 56, bgcolor: avatarColorFor(name) }}>
                  {name.split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase()}
                </Avatar>
                {p.canSetPhoto ? (
                  <>
                    <Button onClick={() => photoInput.current?.click()} disabled={busy === "photo"} sx={{ ...secondaryButtonSx, minHeight: 0, py: 0.6 }}>
                      {busy === "photo" ? <CircularProgress size={14} /> : profile.photoUrl ? "Change photo" : "Add a photo"}
                    </Button>
                    <input ref={photoInput} type="file" hidden accept="image/jpeg,image/png,image/webp" onChange={onPhoto} />
                  </>
                ) : (
                  <Typography sx={{ fontFamily: font, fontSize: "12px", color: textMuted }}>{p.why}</Typography>
                )}
              </Box>
              <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 1.25 }}>
                <TextField size="small" label="First name" value={form.firstName} disabled={!p.canEdit}
                  onChange={(e) => setForm({ ...form, firstName: e.target.value })} sx={inputSx} />
                <TextField size="small" label="Last name" value={form.lastName} disabled={!p.canEdit}
                  onChange={(e) => setForm({ ...form, lastName: e.target.value })} sx={inputSx} />
                <TextField size="small" label="Email" type="email" value={form.email} disabled={!p.canEdit}
                  onChange={(e) => setForm({ ...form, email: e.target.value })} sx={{ ...inputSx, gridColumn: { sm: "1 / -1" } }} />
              </Box>
              {p.canEdit && (
                <Button
                  disabled={busy === "details" || (form.firstName === profile.firstName && form.lastName === profile.lastName && form.email === profile.email)}
                  onClick={() => act("details", () => update_school_staff_api(profile.id, form), { success: "Details saved", refreshList: true })}
                  sx={{ ...primaryButtonSx, mt: 1.5 }}
                >
                  {busy === "details" ? <CircularProgress size={14} sx={{ color: "#fff" }} /> : "Save details"}
                </Button>
              )}
              <Typography sx={{ fontFamily: font, fontSize: "11.5px", color: textMuted, mt: 1.25 }}>
                Joined {profile.createdAt ? moment(profile.createdAt).format("D MMM YYYY") : "—"} · last signed in {profile.lastLogin ? moment(profile.lastLogin).fromNow() : "never"}
              </Typography>
            </Card>

            <Card title="Access">
              {[
                { kind: "reset", label: "Reset password", sub: "Emails them a link to set a new one.", allowed: p.canResetPassword },
                { kind: "suspend", label: suspended ? "Reactivate account" : "Suspend account", sub: suspended ? "Lets them sign in again." : "Stops them signing in. Nothing is deleted.", allowed: p.canSuspend, danger: !suspended },
                { kind: "delete", label: "Delete staff member", sub: "Permanent.", allowed: p.canDelete, danger: true },
              ].map((a) => (
                <Box key={a.kind} sx={{ display: "flex", alignItems: "center", gap: 1, py: 0.9, borderTop: `1px solid ${border}`, "&:first-of-type": { borderTop: "none", pt: 0 } }}>
                  <Box sx={{ flex: 1 }}>
                    <Typography sx={{ fontFamily: font, fontSize: "13px", fontWeight: 700, color: a.danger ? errorColor : textPrimary }}>{a.label}</Typography>
                    <Typography sx={{ fontFamily: font, fontSize: "11.5px", color: textMuted }}>{a.allowed ? a.sub : p.why}</Typography>
                  </Box>
                  <Button
                    disabled={!a.allowed || busy === a.kind}
                    onClick={() => setConfirm({ kind: a.kind })}
                    sx={{ ...secondaryButtonSx, minHeight: 0, py: 0.5, px: 1.4, fontSize: "12.5px", ...(a.danger ? { color: errorColor, borderColor: `${errorColor}55` } : {}) }}
                  >
                    {a.label.split(" ")[0]}
                  </Button>
                </Box>
              ))}
            </Card>
          </>
        )}
      </Box>

      <Dialog open={Boolean(c)} onClose={() => setConfirm(null)} PaperProps={{ sx: dialogPaperSx }} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontFamily: font, fontSize: "15px", fontWeight: 800 }}>{c?.title}</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontFamily: font, fontSize: "13px", color: textSecondary, lineHeight: 1.55 }}>{c?.body}</Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 0 }}>
          <Button onClick={() => setConfirm(null)} sx={secondaryButtonSx}>Keep it</Button>
          <Button
            onClick={() => c?.run()} disabled={Boolean(busy)}
            sx={{ ...primaryButtonSx, ...(c?.danger ? { backgroundColor: errorColor } : {}) }}
          >
            {busy ? <CircularProgress size={14} sx={{ color: "#fff" }} /> : c?.cta}
          </Button>
        </DialogActions>
      </Dialog>
    </CenteredDetailModal>
  );
}
